import { Controller } from "@hotwired/stimulus";
import Inputmask from "inputmask";

export default class extends Controller {
  static targets = ["maskedLicensePlate", "countryCode", "hiddenCountryCode"];

  lastValue = '';
  emulatingEvents = false;
  selectedCountry = null;

  connect() {
    this.selectedCountry = this.countryCodeTarget.value || 'de';
    this.initializeMask();
    this.setupWatchers();
  }

  maskConfig() {
    switch (this.selectedCountry) {
      case 'de':
        return {
          mask: "A{1,3}-B{1,2} 9{1,4}[E]",
          definitions: {
            "9": { validator: "[0-9]", cardinality: 1 },
            "A": { validator: "[A-Za-zÄÖÜäöü]", cardinality: 1, casing: 'upper' },
            "B": { validator: "[A-Za-z]", cardinality: 1, casing: 'upper' },
            "E": { validator: "[E|e|H|h]", cardinality: 1, casing: 'upper' }
          },
          tabThrough: true,
          greedy: true,
          showMaskOnHover: false,
          positionCaretOnTab: false,
          skipOptionalPartCharacter: ' ',
          onKeyDown: this.changeTabDefaultBehavior.bind(this)
        };
      case 'at':
        return {
          mask: "A{1,2}-X{1,8}",
          definitions: {
            "A": { validator: "[A-Za-zÄÖÜäöü]", cardinality: 1, casing: 'upper' },
            "X": { validator: "[A-PR-Za-pr-z0-9]", cardinality: 1, casing: 'upper' },
          },
          tabThrough: true,
          greedy: true,
          keepStatic: true,
          showMaskOnHover: false,
          onKeyDown: this.changeTabDefaultBehavior.bind(this)
        }
      case 'eu':
        return {
          mask: "A{1,10}",
          definitions: {
            "A": { validator: "[A-Za-zÄÖÜäöü0-9 -]", cardinality: 1, casing: 'upper' },
          },
          greedy: true,
          keepStatic: true,
          showMaskOnHover: false
        };
      default:
        return null;
    }
  }

  initializeMask() {
    if (this.hasMaskedLicensePlateTarget) {
      this.destroyMaskIfExists();

      this.maskedLicensePlateTarget.placeholder = this.placeholder()

      this.mask = new Inputmask(this.maskConfig());
      // Register keydown before registering inputMask
      // in order to fire the keydown event before the inputMask
      // is applied to the input field.
      //
      // https://github.com/RobinHerbots/Inputmask/discussions/2836
      //
      this.maskedLicensePlateTarget.addEventListener('keydown', () => {
        this.lastValue = this.maskedLicensePlateTarget.value
      })

      this.mask.mask(this.maskedLicensePlateTarget);

      // Handle click/tap to jump to next section if current section has content
      this.maskedLicensePlateTarget.addEventListener('click', () => {
        // Only proceed if field is already focused (tap on already selected field)
        if (document.activeElement !== this.maskedLicensePlateTarget) return;

        const value = this.maskedLicensePlateTarget.value;

        // Find the last actual content (non-placeholder) character
        const contentMatch = value.match(/[^_\- ]/g);
        if (!contentMatch) return; // No content yet

        const lastContentChar = contentMatch[contentMatch.length - 1];
        const lastContentPos = value.lastIndexOf(lastContentChar);

        // Check if there's an unfilled section ahead (underscore after a separator)
        const remainingValue = value.substring(lastContentPos + 1);
        const hasNextSection = remainingValue.includes('_') &&
                               (remainingValue.includes('-') || remainingValue.includes(' '));

        if (hasNextSection) {
          // Jump to next section by simulating space key
          this.emulatingEvents = true;

          // Position cursor after last content, then dispatch space to skip section
          this.maskedLicensePlateTarget.setSelectionRange(lastContentPos + 1, lastContentPos + 1);

          const spaceEvent = new KeyboardEvent('keydown', { key: ' ', keyCode: 32, bubbles: true });
          this.maskedLicensePlateTarget.dispatchEvent(spaceEvent);
          this.maskedLicensePlateTarget.dispatchEvent(new KeyboardEvent('keyup', { key: ' ', keyCode: 32, bubbles: true }));

          this.emulatingEvents = false;
        }
      });

      this.maskedLicensePlateTarget.addEventListener('keyup', (event) => {
        if (this.emulatingEvents) return

        if (/^[a-zA-Z0-9]$/.test(event.key) && this.lastValue == this.maskedLicensePlateTarget.value) {
          this.emulatingEvents = true
          const spaceKeyDownEvent = new KeyboardEvent('keydown', { key: ' ' })
          this.maskedLicensePlateTarget.dispatchEvent(spaceKeyDownEvent)
          const spaceKeyUpEvent = new KeyboardEvent('keyup', { key: ' ' })
          this.maskedLicensePlateTarget.dispatchEvent(spaceKeyUpEvent)
          const otherKeyDownEvent = new KeyboardEvent('keydown', { key: event.key })
          this.maskedLicensePlateTarget.dispatchEvent(otherKeyDownEvent)
          const otherKeyUpEvent = new KeyboardEvent('keyup', { key: event.key })
          this.maskedLicensePlateTarget.dispatchEvent(otherKeyUpEvent)
          this.emulatingEvents = false
        }
      });
    }
  }

  setupWatchers() {
    this.countryCodeTarget.addEventListener('change', (event) => {
      const newValue = event.target.value;
      this.maskedLicensePlateTarget.value = '';

      this.selectedCountry = newValue;

      // Update hidden field to sync with dropdown
      if (this.hasHiddenCountryCodeTarget) {
        this.hiddenCountryCodeTarget.value = newValue;
      }

      this.initializeMask();
    });

    // Remove the mask placeholders on submit
    this.element.closest('form').addEventListener('submit', () => {
      if (this.maskedLicensePlateTarget.inputmask.isComplete()) {
        this.maskedLicensePlateTarget.inputmask.remove();
      }
    });
  }

  placeholder() {
    switch (this.selectedCountry) {
      case 'de':
        return "B-CD 1234E"
      case 'at':
        return "W-1234AB"
      default:
        return "AA-111-BB"
    }
  }

  // This is a workaround to prevent a tab from breaking the input mask
  // It uses a space key instead, which per default skips the optional part of the mask
  changeTabDefaultBehavior(event) {
    if (event.key === "Tab") {
      event.stopPropagation();
      event.target.dispatchEvent(new KeyboardEvent('keydown', { key: ' ' }));
      event.target.dispatchEvent(new KeyboardEvent('keyup', { key: ' ' }));
    }
  }

  destroyMaskIfExists() {
    if (this.maskedLicensePlateTarget.inputmask) {
      this.maskedLicensePlateTarget.inputmask.remove();
    }
  }
}
